import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';

const ConsultationTimer = ({ startTime, billingRate }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showBillingDetails, setShowBillingDetails] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const getElapsedTime = () => {
    if (!startTime) return { hours: 0, minutes: 0, seconds: 0 };
    
    const elapsed = currentTime - startTime;
    const hours = Math.floor(elapsed / (1000 * 60 * 60));
    const minutes = Math.floor((elapsed % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((elapsed % (1000 * 60)) / 1000);
    
    return { hours, minutes, seconds };
  };

  const formatTime = (time) => {
    return String(time).padStart(2, '0');
  };

  const calculateBillingAmount = () => {
    const { hours, minutes } = getElapsedTime();
    const totalMinutes = hours * 60 + minutes;
    const hourlyAmount = (totalMinutes / 60) * billingRate;
    return Math.round(hourlyAmount);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const { hours, minutes, seconds } = getElapsedTime();
  const billingAmount = calculateBillingAmount();

  return (
    <div className="relative">
      <button
        onClick={() => setShowBillingDetails(!showBillingDetails)}
        className="flex items-center space-x-3 px-4 py-2 bg-secondary-50 hover:bg-secondary-100 rounded-interactive transition-smooth"
        title="Consultation Timer - Click for billing details"
      >
        <div className="flex items-center space-x-2">
          <Icon name="Clock" size={16} className="text-primary" />
          <span className="font-mono text-lg font-bold text-text-primary">
            {formatTime(hours)}:{formatTime(minutes)}:{formatTime(seconds)}
          </span>
        </div>
        
        <div className="text-right">
          <div className="text-sm font-medium text-text-primary">
            {formatCurrency(billingAmount)}
          </div>
          <div className="text-xs text-text-secondary">
            @ {formatCurrency(billingRate)}/hr
          </div>
        </div>
        
        <Icon name="ChevronDown" size={12} className="text-text-secondary" />
      </button>

      {showBillingDetails && (
        <>
          {/* Overlay to close dropdown */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowBillingDetails(false)}
          />
          
          {/* Billing Details Dropdown */}
          <div className="absolute right-0 top-full mt-2 w-72 bg-surface border border-border rounded-lg shadow-dropdown z-20">
            <div className="p-4 border-b border-border">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Calculator" size={20} className="text-primary" />
                <h3 className="font-medium text-text-primary">Billing Information</h3>
              </div>
              <p className="text-sm text-text-secondary">
                Real-time consultation billing tracker
              </p>
            </div>

            <div className="p-4 space-y-4">
              {/* Time Breakdown */}
              <div>
                <h4 className="text-sm font-medium text-text-primary mb-2">Time Breakdown</h4>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="p-2 bg-secondary-50 rounded">
                    <div className="text-lg font-bold text-primary">{formatTime(hours)}</div>
                    <div className="text-xs text-text-secondary">Hours</div>
                  </div>
                  <div className="p-2 bg-secondary-50 rounded">
                    <div className="text-lg font-bold text-primary">{formatTime(minutes)}</div>
                    <div className="text-xs text-text-secondary">Minutes</div>
                  </div>  
                  <div className="p-2 bg-secondary-50 rounded">
                    <div className="text-lg font-bold text-primary">{formatTime(seconds)}</div>
                    <div className="text-xs text-text-secondary">Seconds</div>
                  </div>
                </div>
              </div>

              {/* Billing Details */}
              <div>
                <h4 className="text-sm font-medium text-text-primary mb-2">Billing Details</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-text-secondary">Hourly Rate:</span>
                    <span className="font-medium">{formatCurrency(billingRate)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-text-secondary">Duration:</span>
                    <span className="font-medium">
                      {hours > 0 && `${hours}h `}{minutes}m {seconds}s
                    </span>
                  </div>
                  <div className="flex justify-between text-sm pt-2 border-t border-border">
                    <span className="font-medium text-text-primary">Current Total:</span>
                    <span className="font-bold text-primary">{formatCurrency(billingAmount)}</span>
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="text-xs text-text-secondary bg-secondary-50 p-3 rounded">
                <div className="flex items-center space-x-1 mb-1">
                  <Icon name="Info" size={10} />
                  <span className="font-medium">Billing Notes:</span>
                </div>
                <ul className="space-y-1 ml-3">
                  <li>• Time is tracked automatically during consultation</li>
                  <li>• Billing increments in 6-minute intervals</li>
                  <li>• Recording and transcript included in consultation fee</li>
                </ul>
              </div>
            </div>

            <div className="p-4 border-t border-border">
              <button className="w-full px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-interactive transition-smooth text-sm font-medium">
                Generate Invoice
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ConsultationTimer;